import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, inject } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import { CheckboxComponent, FormGroupComponent, InputComponent, NumberComponent, SelectComponent, SwitcherComponent } from 'mbs-ui-kit';
import { ConnectSettingsForm, JoinSessionMode, PinLength, ProxyMode } from 'mbs-ui-shared@models/connect/connect-settings.models';
import { ConnectSettingsWrapper } from 'mbs-ui-shared@models/connect/connect-settings.wrapper';
import { PasswordInputType } from 'mbs-ui-shared@models/Settings.enum';
import { ProviderVerificationService } from 'mbs-ui-shared@services/provider-verification.service';
import {
  getJoinSessionModeItems,
  getLoggingLevelItems,
  getProxyModeItems,
  getTimeUnits,
  getUnattendedAccessDisconnectModeOptions
} from 'mbs-ui-shared@utils/ra-settings';

enum ViewMode {
  Page = 'page',
  Panel = 'panel'
}

@Component({
  standalone: true,
  selector: 'mbs-connect-settings-form',
  templateUrl: './connect-settings.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    FormGroupComponent,
    I18NextPipe,
    CheckboxComponent,
    InputComponent,
    NumberComponent,
    SelectComponent,
    SwitcherComponent
  ]
})
export class ConnectSettingsComponent {
  private i18Next = inject(I18NextPipe);
  private providerVerification = inject(ProviderVerificationService);
  private destroyRef = inject(DestroyRef);

  public readonly unattendedAccessDisconnectOptions = getUnattendedAccessDisconnectModeOptions(this.i18Next);
  public readonly timeUnits = getTimeUnits(this.i18Next);
  public readonly joinSessionModeSelectItems = getJoinSessionModeItems(this.i18Next);
  public readonly proxyModeSelectItems = getProxyModeItems(this.i18Next);
  public readonly pinLengthSelectItems = [PinLength.Four, PinLength.Six, PinLength.Eight, PinLength.Ten];
  public readonly loggingLevelSelectItems = getLoggingLevelItems(this.i18Next);
  public readonly viewModeEnum = ViewMode;
  public readonly Number = Number;

  public unattendedPasswordInputType = PasswordInputType.PASSWORD;
  public configPasswordInputType = PasswordInputType.PASSWORD;
  public basicAuthPasswordInputType = PasswordInputType.PASSWORD;

  @Input() set form(value: ConnectSettingsForm) {
    this.connectForm = value;
    this.guardDirectAccessToggle();
  }
  get form(): ConnectSettingsForm {
    return this.connectForm;
  }

  private connectForm: ConnectSettingsForm;
  private guardedDirectAccessControl: AbstractControl<boolean>;

  @Input() formState: ConnectSettingsWrapper;
  @Input({ transform: (value: string): ViewMode => value as ViewMode }) mode: ViewMode = ViewMode.Page;

  private guardDirectAccessToggle(): void {
    const control = this.connectForm?.get('managedSystem.allowRAUnattendedAccess');

    if (!control || control === this.guardedDirectAccessControl) return;

    this.guardedDirectAccessControl = control;
    this.providerVerification.guardDangerousToggle(control, this.destroyRef);
  }

  isUseBasicAuthSectionVisible(): boolean {
    const proxyMode = this.formState?.proxy?.mode;

    return proxyMode === ProxyMode.AutoDetect || proxyMode === ProxyMode.Manual;
  }

  isProxyServerHostVisible(): boolean {
    const proxyMode = this.formState.proxy?.mode;

    return proxyMode === ProxyMode.Manual;
  }

  isUseBasicAuthUserPasswordSectionVisible(): boolean {
    return this.formState?.proxy?.useBasicAuth;
  }

  isPinLengthVisible(): boolean {
    const joinSessionMode = this.formState?.security?.joinSessionMode;

    return joinSessionMode === JoinSessionMode.Pin || joinSessionMode === JoinSessionMode.DialogAndPin;
  }

  isUnattendedAccessPasswordVisible(): boolean {
    return this.formState?.security?.customPassword?.enabled;
  }

  isConfigPasswordVisible(): boolean {
    return this.formState?.configPassword?.enabled;
  }

  getPasswordInputType(inputType: PasswordInputType): PasswordInputType {
    return inputType === PasswordInputType.PASSWORD ? PasswordInputType.TEXT : PasswordInputType.PASSWORD;
  }
}
